import React from 'react'
import clsx from 'clsx'
import { ArrowDown2, Call, Sms, User as UserIcon } from 'iconsax-react'
import { useTheme } from '@mui/material'

import MergeContactSearch from '../MergeContactSearch'
import MediaAvatar from '../../../atoms/MediaAvatar'
import Button from '../../../atoms/Button2'
import OverflowText from '../../../atoms/OverflowText'
import PhoneFlag from '../../../atoms/PhoneFlag'
import OpenConversationV2 from '../../EHAccordion/OpenConversationsV2/OpenConversationV2'

import { getFormatted } from '../../../../utilities/phoneNumber'
import { reformatLatestActivity } from '../../../../providers/SocketProvider/helpers'
import { newestDateComparator } from '../../../organisms/Conversations'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import useStyles from './styles'

export type User = APISimpleUser & { channels: APIChannel[] }
interface MergeContactBoxProps {
  primaryContact?: User
  primary?: boolean
  contact: null | User
  onSelect?: (user: User) => void
  mobile?: boolean
}

const MergeContactBox: React.FC<MergeContactBoxProps> = ({
  contact,
  primary,
  primaryContact,
  onSelect,
  mobile
}) => {
  const theme = useTheme()
  const { conversations } = useQueueState()

  const rootRef = React.useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = React.useState(false)

  const related = React.useMemo(() => {
    if (!contact || !primary) {
      return []
    }

    const all = Object.values(conversations) as any[]
    const filtered = all.filter(conv => conv.owner_id === contact.id)
    const formatted = filtered.map(conv => {
      const latest_activity = (conv.latest_activity ?? []).map(
        reformatLatestActivity
      )
      return { ...conv, latest_activity }
    })

    return formatted.sort(newestDateComparator)
  }, [contact, primary, conversations])

  const classes = useStyles()

  const toggleExpand = React.useCallback(() => {
    setExpanded(old => !old)
  }, [])

  return (
    <div
      ref={rootRef}
      className={clsx(classes.root, mobile && primary && classes.bottomSpacing)}
    >
      {!contact ? (
        <MergeContactSearch
          primaryContact={primaryContact}
          rootRef={rootRef}
          onSelect={onSelect}
        />
      ) : (
        <>
          <div className={classes.avatarHolder}>
            <MediaAvatar user={contact} hideCard className='avatar' />
          </div>

          {!primary && (
            <Button onClick={() => onSelect(null)} className={classes.button}>
              Change
            </Button>
          )}

          <div className={classes.detailTitle}>Contact Detail</div>

          <div className={classes.detailRow}>
            <UserIcon
              variant='Bold'
              size={theme.size.sm}
              className={classes.detailIcon}
            />

            <div className={classes.detailName}>Name</div>

            <div className={classes.detailValue}>{contact.name}</div>
          </div>

          {contact.channels.map(channel => {
            let Icon = Call
            let value = channel.value
            if (channel.type === 'email') {
              Icon = Sms
              value = getFormatted(value)
            }

            return (
              <div key={channel.id} className={classes.detailRow}>
                <Icon
                  size={theme.size.base}
                  variant='Bold'
                  color='#8F8F8F'
                  className={classes.detailIcon}
                />

                <OverflowText className={classes.detailName}>
                  {channel.name}
                </OverflowText>

                {['sms_voice', 'voice_only'].includes(channel.type) && (
                  <PhoneFlag
                    value={channel.value}
                    className={classes.detailFlag}
                  />
                )}

                <OverflowText className={classes.detailValue}>
                  {value}
                </OverflowText>
              </div>
            )
          })}

          {primary && related.length > 0 && (
            <>
              <div onClick={toggleExpand} className={classes.showMore}>
                {expanded ? 'Hide' : 'See'} Conversations{' '}
                <ArrowDown2
                  variant='Bold'
                  size={theme.size.sm}
                  className='icon'
                  style={{ transform: `rotate(${expanded ? 180 : 0}deg)` }}
                />
              </div>

              {expanded && (
                <div className={classes.conversations}>
                  {related.map(convo => (
                    <OpenConversationV2
                      key={convo.session}
                      conversation={convo}
                      disableInteract
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default MergeContactBox
